import { StoreEntryFlowEditor } from "../components/store/StoreEntryFlowEditor";
import { useState, useEffect, useRef } from "react";
import {
  ExternalLink,
  Copy,
  Check,
  Store,
  AlertCircle,
  BookOpen,
  Layout,
} from "lucide-react";
import QRCode from "qrcode";
import { useUser, useAuthStore } from "../store/useAuthStore";
import apiClient from "../lib/api";
import { toast } from "../lib/toast";
import { CustomStoreEditor } from "../components/store/custom/CustomStoreEditor";
import { StorefrontThemeEditor } from "../components/store/StorefrontThemeEditor";
import { StorefrontBrandingEditor } from "../components/store/StorefrontBrandingEditor";

export default function PhysicianStore() {
  const user = useUser();
  const { getCurrentUser, patchUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState(() =>
    user?.physicianData?.storefrontVariant === "custom" ||
    new URLSearchParams(window.location.search).has("customPreviewRevision")
      ? "builder"
      : "settings",
  );
  const [educationalStartRequest, setEducationalStartRequest] = useState(0);
  const [copied, setCopied] = useState(false);
  const [savingVariant, setSavingVariant] = useState(false);

  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  // DTP is on by default — only an explicit `false` turns the store off
  const dtpEnabled = user?.physicianData?.directToPatientEnabled !== false;
  const storeUrl = `${window.location.origin}/dtp/${user?._id}`;
  const variant = user?.physicianData?.storefrontVariant || "lightweight";

  const setVariant = async (next: "lightweight" | "educational") => {
    if (next === variant) return;
    setSavingVariant(true);
    try {
      const res = await apiClient.updateStorefrontVariant(next);
      if (res.success) {
        // Updating this field in place keeps the open custom draft mounted.
        if (user?.physicianData)
          patchUser({
            physicianData: { ...user.physicianData, storefrontVariant: next },
          });
        else await getCurrentUser();
        toast.success(
          `Switched to Bloom's ${next === "educational" ? "Educational" : "Lightweight"} template`,
        );
      } else {
        toast.error(res.error || "Failed to update layout");
      }
    } catch {
      toast.error("Failed to update layout. Please try again.");
    } finally {
      setSavingVariant(false);
    }
  };

  useEffect(() => {
    if (dtpEnabled && qrCanvasRef.current) {
      QRCode.toCanvas(qrCanvasRef.current, storeUrl, { width: 180, margin: 2 });
    }
  }, [dtpEnabled, storeUrl, activeTab]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(storeUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!dtpEnabled) {
    return (
      <div className="max-w-2xl mx-auto mt-16 text-center">
        <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Store Not Enabled
          </h2>
          <p className="text-gray-500">
            Direct-to-patient ordering is not enabled for your account. Contact
            your administrator to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">My Store</h1>
            <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
              {variant === "custom"
                ? "Your custom store is live"
                : `Bloom template · ${variant === "educational" ? "Educational" : "Lightweight"}`}
            </span>
          </div>
          <p className="text-gray-500 mt-1">
            Build your storefront, manage its appearance, and share it with
            patients.
          </p>
        </div>
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          <ExternalLink className="h-4 w-4" /> Open live store
        </a>
      </div>
      <nav
        aria-label="Store workspace"
        className="flex gap-6 border-b border-gray-200"
      >
        {[
          ["settings", "Storefront"],
          ["builder", "Custom builder"],
          ["share", "Share store"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-current={activeTab === id ? "page" : undefined}
            onClick={() => setActiveTab(id)}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${activeTab === id ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-900"}`}
          >
            {label}
          </button>
        ))}
      </nav>
      <div hidden={activeTab !== "builder"}>
        <CustomStoreEditor
          clinicId={user?._id || ""}
          clinicName={user?.profile?.clinicName || "Your clinic"}
          onPublished={getCurrentUser}
          educationalStartRequest={educationalStartRequest}
          liveVariant={variant}
          templateHeadline={
            (user?.physicianData as any)?.storefrontTheme?.headline
          }
          templateIntroduction={
            (user?.physicianData as any)?.storefrontTheme?.description
          }
        />
      </div>
      <div hidden={activeTab !== "share"} className="max-w-3xl">
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
          {/* Store URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Storefront Link
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 font-mono text-sm text-gray-800 truncate">
                {storeUrl}
              </div>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-4 py-3 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-green-600">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 text-gray-500" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* QR Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              QR Code
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Print or display this QR code so patients can scan it to visit
              your store.
            </p>
            <div className="inline-block bg-white border border-gray-200 rounded-lg p-4">
              <canvas ref={qrCanvasRef} />
            </div>
          </div>

          {/* Open Store button */}
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Store className="w-4 h-4" />
            Open Store
            <ExternalLink className="w-3.5 h-3.5 opacity-70" />
          </a>
        </div>
      </div>
      <div hidden={activeTab !== "settings"} className="max-w-4xl">
        <StoreEntryFlowEditor />
        {/* Storefront layout */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900">
            Bloom templates
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Use a ready-made Bloom layout with your branding. Bloom manages the
            page structure. Selecting a template changes your live store
            immediately; your custom draft is kept.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <button
              onClick={() => setVariant("lightweight")}
              disabled={savingVariant}
              className={`text-left rounded-xl border p-5 transition-colors ${
                variant === "lightweight"
                  ? "border-blue-500 ring-2 ring-blue-100 bg-blue-50/40"
                  : "border-gray-200 hover:border-gray-300"
              } ${savingVariant ? "opacity-60 cursor-wait" : ""}`}
            >
              <div className="flex items-center gap-2">
                <Layout className="w-4 h-4 text-gray-700" />
                <span className="font-medium text-gray-900">Lightweight</span>
                {variant === "lightweight" && (
                  <Check className="w-4 h-4 text-blue-600 ml-auto" />
                )}
              </div>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                Fast browse, fewest clicks to checkout. Best when patients
                already know what they want.
              </p>
            </button>

            <button
              onClick={() => setVariant("educational")}
              disabled={savingVariant}
              className={`text-left rounded-xl border p-5 transition-colors ${
                variant === "educational"
                  ? "border-blue-500 ring-2 ring-blue-100 bg-blue-50/40"
                  : "border-gray-200 hover:border-gray-300"
              } ${savingVariant ? "opacity-60 cursor-wait" : ""}`}
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-gray-700" />
                <span className="font-medium text-gray-900">Educational</span>
                {variant === "educational" && (
                  <Check className="w-4 h-4 text-blue-600 ml-auto" />
                )}
              </div>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                Categories, drug explainers, and a dose finder. Best for
                patients new to a treatment area.
              </p>
            </button>
          </div>
        </div>

        <div className="mt-6 p-6 border border-blue-200 rounded-xl bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-gray-900">
              Your custom store
            </h2>
            {variant === "custom" && (
              <span className="text-sm font-medium text-green-700">
                Currently live
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Start from Educational, then make the homepage and product pages
            your own. Keep category browsing and treatment explainers, and
            control your content, imagery, and section order.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Your changes stay in a draft until you review and publish. You can
            return to a Bloom template at any time.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              className="rounded-lg bg-blue-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-blue-700"
              onClick={() => {
                setEducationalStartRequest((n) => n + 1);
                setActiveTab("builder");
              }}
            >
              Customize Educational
            </button>
            <button
              className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium hover:bg-gray-50"
              onClick={() => setActiveTab("builder")}
            >
              Open custom builder
            </button>
          </div>
        </div>

        {variant !== "custom" && (
          <>
            <StorefrontBrandingEditor onSaved={getCurrentUser} />
            <StorefrontThemeEditor
              initial={(user?.physicianData as any)?.storefrontTheme}
              onSaved={getCurrentUser}
            />
          </>
        )}
      </div>
    </div>
  );
}
